import { Component, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { CheckboxModule } from 'primeng/checkbox'; // cspell:disable-line
import { TooltipModule } from 'primeng/tooltip'; // cspell:disable-line
import { LucideAngularModule } from 'lucide-angular';
import { FormatCO2ePipe } from 'src/app/pipes/format-co2e.pipe';
import { DurationFormatPipe } from 'src/app/pipes/duration-format.pipe';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import { EcoscoreComponent } from 'src/app/travel-board/components/ecoscore/ecoscore.component';
import {
  AlternativeChoice,
  AlternativeMode,
  DirectFlightTipData,
  EcoAccommodationTipData,
  HighEmissionActivityTipData,
  ReductionTip,
  TransportAlternativeTipData,
  TripDurationTipData,
} from '../../../models/reduction-tip';
import {
  getAlternative,
  getAvailableAlternativeModes,
  getDefaultChoice,
  isSelectableTip,
} from '../../../utils/reduction-tip-savings';
import { ReductionTipsService } from '../../../services/reduction-tips.service';
import { ALTERNATIVE_ICON } from '../../../constants/reduction-tip-icons';
import { RouteLegsComponent } from '../route-legs/route-legs.component';

/**
 * One tip, as a single row of its stats category's tips card: the element it
 * rewrites, what it would become, and what that saves. A flight answers a
 * single question — what to replace it with — through one chip per ground
 * alternative after a "keep the flight" chip, the steps of the chosen
 * itinerary below them; the flight's row folds all of it away. Informational
 * tips carry no saving and read as one sentence.
 */
@Component({
  selector: 'app-reduction-tip-item',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    CheckboxModule,
    TooltipModule,
    LucideAngularModule,
    FormatCO2ePipe,
    DurationFormatPipe,
    EcoscoreComponent,
    RouteLegsComponent,
  ],
  providers: [DurationFormatPipe],
  templateUrl: './reduction-tip-item.component.html',
})
export class ReductionTipItemComponent {
  tip = input.required<ReductionTip>();

  private translocoService = inject(TranslocoService);
  protected tipsService = inject(ReductionTipsService);
  private formatCo2e = inject(FormatCo2eService);
  private durationPipe = inject(DurationFormatPipe);

  /**
   * Whether a flight's chips are shown. Folded by default to keep a card of
   * several flights short; the folded row still tells the comparison apart —
   * the mode simulated, else how many there are and the best share — so it
   * does not go unnoticed.
   */
  expanded = signal(false);

  selected = computed(() => this.tipsService.isSelected(this.tip().id));
  selectable = computed(() => isSelectableTip(this.tip()));

  /** What the tip rewrites: "Aller Paris → Nice", "Hôtel Promenade". */
  elementLabel = computed(() => this.tipsService.elementLabelOf(this.tip()));

  alternativeData = computed(() =>
    this.tip().type === 'transport_alternative'
      ? (this.tip().data as TransportAlternativeTipData)
      : null,
  );

  /**
   * Alternative the flight's chips land on: the user's choice, else the one
   * emitting the least. Simulated only while the tip is selected — otherwise
   * the "keep the flight" chip is the one checked.
   */
  effectiveChoice = computed<AlternativeChoice | null>(() => {
    const data = this.alternativeData();
    if (!data) return null;
    return this.tipsService.choiceOf(this.tip().id) ?? getDefaultChoice(data);
  });

  /** The flight as it stands — the yardstick on the "keep the flight" chip. */
  flightEmissions = computed(() => {
    const data = this.alternativeData();
    return data
      ? this.tipsService.fromPerPerson(data.currentEmissionsPerPerson)
      : 0;
  });

  /**
   * No ground itinerary behind the figures: every mode is an estimate off the
   * flight's distance. Said once, on the flight, rather than on every chip.
   */
  isEstimate = computed(() => {
    const data = this.alternativeData();
    return !!data && !data.groundRoutes;
  });

  /**
   * One chip per mode on offer. Each shows the itinerary it would simulate:
   * the one picked in the dialog when the choice is on that mode, the default
   * one otherwise. The mode emitting the least is flagged once there is a
   * comparison to make.
   */
  modeRows = computed(() => {
    const data = this.alternativeData();
    if (!data) return [];
    const current = this.effectiveChoice();
    const selected = this.selected();
    const modes = getAvailableAlternativeModes(data);
    const lowest = modes.length > 1 ? getDefaultChoice(data)?.mode : undefined;

    return modes.map((mode) => {
      const choice: AlternativeChoice = {
        mode,
        routeIndex: current?.mode === mode ? current.routeIndex : 0,
      };
      const alternative = getAlternative(data, choice)!;
      const route = alternative.route;
      return {
        choice,
        icon: ALTERNATIVE_ICON[mode],
        label: this.tipsService.alternativeLabel(mode, route),
        route,
        /** A road itinerary has no legs to list — its length stands in. */
        roadKm:
          route && !route.legs.length ? Math.round(route.distanceKm) : null,
        routeCount: data.alternatives[mode]?.routes?.length ?? 0,
        savingsPercent: alternative.savingsPercent,
        isLowest: mode === lowest,
        isChosen: selected && current?.mode === mode,
      };
    });
  });

  /** The "keep the flight" chip — the one without a mode row — then one per mode. */
  chips = computed(() => [
    {
      value: 'keep',
      icon: 'plane',
      titleKey: 'reductionTips.keepFlight',
      isChosen: !this.selected(),
      row: null,
    },
    ...this.modeRows().map((row) => ({
      value: row.choice.mode,
      icon: row.icon,
      titleKey: `reductionTips.chip.${row.choice.mode}`,
      isChosen: row.isChosen,
      row,
    })),
  ]);

  /** The chip simulated, whose steps unfold below — none while the flight is kept. */
  chosenRow = computed(
    () => this.modeRows().find((row) => row.isChosen) ?? null,
  );

  /** The largest share a mode avoids — "up to" on the folded row of a kept flight. */
  bestSavingsPercent = computed(() =>
    Math.max(0, ...this.modeRows().map((row) => row.savingsPercent)),
  );

  /**
   * Where the flight runs, next to its name. Without airport codes the API
   * sends a duration or a distance instead, which still tells two legs apart.
   */
  flightRoute = computed(() => {
    const data = this.alternativeData();
    if (!data) return null;
    if (data.flightRoute) return data.flightRoute;
    if (data.transportDuration) {
      return this.durationPipe.transform(data.transportDuration);
    }
    return `${Math.round(data.distance)} km`;
  });

  /** "en vol direct CDG → BUD", "en hébergement éco-responsable". */
  actionLabel = computed(() => {
    const tip = this.tip();
    switch (tip.type) {
      case 'direct_flight':
        return this.translocoService.translate(
          'reductionTips.directFlight.action',
          { route: (tip.data as DirectFlightTipData).flightRoute },
        );
      case 'eco_accommodation':
        return this.translocoService.translate(
          'reductionTips.ecoAccommodation.action',
        );
      default:
        return '';
    }
  });

  /** kg CO₂e avoided over the whole trip, on the basis the page displays. */
  displaySavings = computed(() =>
    this.tipsService.toDisplayed(this.tipsService.savingOf(this.tip())),
  );

  savingsPercent = computed(() => {
    const tip = this.tip();
    switch (tip.type) {
      case 'direct_flight':
      case 'eco_accommodation':
        return (tip.data as DirectFlightTipData | EcoAccommodationTipData)
          .savingsPercent;
      default:
        return 0;
    }
  });

  /**
   * Eco-score today and the longer stays that raise it, one per grade gained.
   * The API only sends the tip when there is at least one.
   */
  durationData = computed(() => {
    const tip = this.tip();
    return tip.type === 'trip_duration'
      ? (tip.data as TripDurationTipData)
      : null;
  });

  /** The sentence an informational tip reads as. */
  hint = computed(() => {
    const tip = this.tip();
    if (tip.type === 'high_emission_activity') {
      const data = tip.data as HighEmissionActivityTipData;
      return this.translocoService.translate(
        'reductionTips.highEmissionActivity.hint',
        {
          activity: this.elementLabel(),
          impact: this.formatCo2e.format(data.impactPerPerson),
        },
      );
    }
    const duration = this.durationData();
    if (duration) {
      return this.translocoService.translate(
        'reductionTips.tripDuration.hint',
        {
          days: duration.currentDays,
          current: this.formatCo2e.format(duration.currentIntensity),
        },
      );
    }
    return '';
  });

  toggleSelected(): void {
    this.tipsService.setSelection([this.tip().id], !this.selected());
  }

  /** The "keep the flight" chip: the flight is no longer simulated away. */
  keepFlight(): void {
    this.tipsService.setSelection([this.tip().id], false);
  }

  /** Picking a mode simulates it on the itinerary its chip shows. */
  chooseMode(choice: AlternativeChoice): void {
    this.tipsService.chooseAlternative(this.tip().id, choice);
  }

  openRoutes(mode: AlternativeMode): void {
    this.tipsService.openRoutes(this.tip().id, mode);
  }
}
